import { Button } from './button/Button';
import type { GameEvent } from '../api/games';
import skeletonImg from '../assets/skeleton.png';
import handcuffsImg from '../assets/handcuffs.png';
import styles from './SpectatorView.module.css';

type Status = 'dead' | 'arrested' | 'left';

const IMAGE: Record<Status, string> = {
  dead: skeletonImg,
  arrested: handcuffsImg,
  left: skeletonImg,
};

const LABEL: Record<Status, string> = {
  dead: 'You have been eliminated',
  arrested: 'You have been arrested',
  left: 'You left the game',
};

type Props = {
  status: Status;
  activePlayers: number;
  liveEvents: GameEvent[];
  gameFinished: boolean;
  onGoHome: () => void;
};

function formatEvent(e: GameEvent): string {
  const actor = e.actor ?? '?';
  const target = e.target ?? '?';
  switch (e.type) {
    case 'join':
      return `${actor} joined`;
    case 'leave':
      return `${actor} left`;
    case 'kick':
      return `${target} was kicked`;
    case 'start':
      return 'Game started';
    case 'end':
      return 'Game over';
    case 'restart':
      return 'New round started';
    case 'kill_request':
      return `${actor} made a move on ${target}`;
    case 'kill_accept':
      return `${target} confirmed the kill by ${actor}`;
    case 'kill_decline':
      return `${target} declined the kill claim`;
    case 'kill':
      return `${actor} eliminated ${target}`;
    case 'arrest':
      return `${target} was arrested`;
    case 'witness_report':
      return `Report filed against ${target}`;
    case 'witness_response':
      return `${actor} responded to a report`;
    case 'mod_ruling':
      return `Moderator made a ruling`;
    case 'weapon_suggest':
      return `${actor} suggested a weapon`;
    case 'weapon_approve':
      return 'A weapon was approved';
    case 'weapon_decline':
      return 'A weapon was declined';
    default:
      return e.type;
  }
}

export function SpectatorView({
  status,
  activePlayers,
  liveEvents,
  gameFinished,
  onGoHome,
}: Props) {
  return (
    <div className={styles.wrapper}>
      <img src={IMAGE[status]} className={styles.statusImg} alt={status} />
      <p className={styles.label}>{LABEL[status]}</p>

      {!gameFinished && (
        <div className={styles.countdown}>
          <span className={styles.countNum}>{activePlayers}</span>
          <span className={styles.countLabel}>
            {activePlayers === 1 ? 'player' : 'players'} remaining
          </span>
        </div>
      )}

      {gameFinished && <p className={styles.finishedNote}>The game has ended.</p>}

      {liveEvents.length > 0 && (
        <div className={styles.feed}>
          {liveEvents.map((e, i) => (
            <p key={i} className={styles.feedLine}>
              {formatEvent(e)}
            </p>
          ))}
        </div>
      )}

      <Button onClick={onGoHome}>Go home</Button>
    </div>
  );
}
